import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { clearAuthToken, setAuthToken, setOnUnauthorized } from '@/api';
import type { AccountResponse } from '@/types';

const TOKEN_KEY = 'ig_auth_token';
const ACCOUNT_KEY = 'ig_auth_account';

export interface AuthState {
  token: string | null;
  account: AccountResponse | null;
  isAuthenticated: boolean;
}

export interface AuthContextValue extends AuthState {
  login: (token: string, account: AccountResponse) => void;
  logout: () => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

function loadStoredAuth(): { token: string | null; account: AccountResponse | null } {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const raw = localStorage.getItem(ACCOUNT_KEY);
    const account = raw ? (JSON.parse(raw) as AccountResponse) : null;

    return { token, account };
  } catch {
    return { token: null, account: null };
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => loadStoredAuth().token);
  const [account, setAccount] = useState<AccountResponse | null>(() => loadStoredAuth().account);
  const queryClient = useQueryClient();

  // Clearing the query cache on every account change stops one account seeing another's data.
  const login = useCallback(
    (newToken: string, newAccount: AccountResponse) => {
      queryClient.clear();
      localStorage.setItem(TOKEN_KEY, newToken);
      localStorage.setItem(ACCOUNT_KEY, JSON.stringify(newAccount));
      setAuthToken(newToken);
      setToken(newToken);
      setAccount(newAccount);
    },
    [queryClient],
  );

  const logout = useCallback(() => {
    queryClient.clear();
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ACCOUNT_KEY);
    clearAuthToken();
    setToken(null);
    setAccount(null);
  }, [queryClient]);

  useEffect(() => {
    if (token) {
      setAuthToken(token);
    }

    setOnUnauthorized(() => {
      logout();
    });
  }, [token, logout]);

  const value = useMemo<AuthContextValue>(
    () => ({
      token,
      account,
      isAuthenticated: token !== null && account !== null,
      login,
      logout,
    }),
    [token, account, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext(): AuthContextValue {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }

  return context;
}
